// Inline image cycling. Only the hovered, expanded image owns a keyboard
// listener; the post renderer's removal observer cleans up a detached owner.
let activeNavigation = null;

export function sweepImageNavigation() {
  if (activeNavigation && !activeNavigation.frame.isConnected) {
    activeNavigation.stop();
  }
}

export function attachImageNavigation(img, { getImages, onSelect, isAvailable }) {
  const frame = img.parentElement;
  let index = 0;
  let selectedId = null;
  let expanded = false;
  const buttons = [];

  const current = () => {
    const images = getImages();
    const found = selectedId == null ? -1 : images.findIndex((image) => image.id === selectedId);
    index = found >= 0 ? found : Math.min(index, Math.max(0, images.length - 1));
    selectedId = images[index]?.id ?? null;
    return images[index];
  };

  const step = (direction) => {
    if (!expanded || !isAvailable()) return;
    current();
    const images = getImages();
    if (images.length < 2) return;
    index = (index + direction + images.length) % images.length;
    selectedId = images[index].id ?? null;
    onSelect(images[index]);
  };

  const stop = () => {
    document.removeEventListener("keydown", onKey);
    if (activeNavigation?.stop === stop) activeNavigation = null;
  };

  const onKey = (event) => {
    if (!frame.isConnected || !frame.matches(":hover") || !expanded) {
      stop();
      return;
    }
    if (event.defaultPrevented || event.isComposing || event.ctrlKey || event.metaKey
      || event.altKey || event.shiftKey || !isAvailable() || getImages().length < 2) return;
    // A hovered image must not steal cursor movement from the composer or
    // other inputs, including editable fields inside custom UI.
    if (event.target.closest?.("input, textarea, select, [contenteditable]:not([contenteditable='false'])")) return;
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    event.stopPropagation();
    step(event.key === "ArrowRight" ? 1 : -1);
  };

  const sync = () => {
    const visible = expanded && getImages().length > 1;
    frame.classList.toggle("is-expanded", expanded);
    for (const button of buttons) button.hidden = !visible;
    stop();
    if (visible && frame.isConnected && frame.matches(":hover")) {
      activeNavigation?.stop();
      activeNavigation = { frame, stop };
      document.addEventListener("keydown", onKey);
    }
  };

  for (const [direction, label] of [[-1, "Previous image"], [1, "Next image"]]) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "post-image-nav " + (direction < 0 ? "is-prev" : "is-next");
    button.title = label;
    button.setAttribute("aria-label", label);
    button.innerHTML = `<i class="fa-solid fa-chevron-${direction < 0 ? "left" : "right"}" aria-hidden="true"></i>`;
    button.hidden = true;
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      step(direction);
    });
    frame.appendChild(button);
    buttons.push(button);
  }
  frame.addEventListener("mouseenter", sync);
  frame.addEventListener("mouseleave", stop);

  return {
    current,
    setExpanded(value) {
      expanded = value;
      sync();
    },
  };
}
